import React, { useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, Card, CardContent,
  Avatar, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem, IconButton, Tooltip, Chip, Paper, Grid
} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import {
  ArrowBack as ArrowBackIcon, Send as SendIcon, Delete as DeleteIcon,
  VisibilityOff as HideIcon, Warning as WarningIcon, MoreVert as MoreVertIcon,
  Person as PersonIcon, Business as BusinessIcon, Message as MessageIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Conversation, Message, ModerationAction } from '../types/moderation';
import apiService from '../services/api';

interface ConversationViewerProps {
  conversation: Conversation;
  onBack: () => void;
}

const ConversationViewer: React.FC<ConversationViewerProps> = ({ conversation, onBack }) => {
  const [interventionDialogOpen, setInterventionDialogOpen] = useState(false);
  const [interventionMessage, setInterventionMessage] = useState('');
  const [moderationDialogOpen, setModerationDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [moderationAction, setModerationAction] = useState<ModerationAction['action']>('delete');
  const [moderationReason, setModerationReason] = useState('');

  // Charger les détails complets de la conversation
  const { data: conversationDetails, isLoading: detailsLoading } = useQuery({
    queryKey: ['conversation-details', conversation.id],
    queryFn: () => apiService.getConversationDetails(conversation.id),
    select: (response) => response.data,
  });

  const { data: messagesData, isLoading: messagesLoading, refetch: refetchMessages } = useQuery({
    queryKey: ['conversation-messages', conversation.id],
    queryFn: () => apiService.getConversationMessages(conversation.id, { limit: 100 }),
    select: (response) => response.data,
  });

  const moderateMessageMutation = useMutation({
    mutationFn: ({ messageId, action }: { messageId: number; action: ModerationAction }) =>
      apiService.moderateMessage(messageId, action),
    onSuccess: () => {
      refetchMessages();
      setModerationDialogOpen(false);
      setSelectedMessage(null);
    },
  });

  const interveneMutation = useMutation({
    mutationFn: (message: { content: string }) =>
      apiService.interveneInConversation(conversation.id, message),
    onSuccess: () => {
      refetchMessages();
      setInterventionDialogOpen(false);
      setInterventionMessage('');
    },
  });

  const handleModerateMessage = (message: Message) => {
    setSelectedMessage(message);
    setModerationDialogOpen(true);
  };

  const handleModerationSubmit = () => {
    if (!selectedMessage) return;

    const action: ModerationAction = {
      action: moderationAction,
      reason: moderationReason,
    };

    moderateMessageMutation.mutate({
      messageId: selectedMessage.id,
      action,
    });
  };

  const handleInterventionSubmit = () => {
    if (!interventionMessage.trim()) return;

    interveneMutation.mutate({
      content: interventionMessage.trim(),
    });
  };

  const formatMessageTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getModerationActionLabel = (action: ModerationAction['action']) => {
    switch (action) {
      case 'delete': return 'Supprimer';
      case 'hide': return 'Masquer';
      case 'warn': return 'Avertir';
      default: return action;
    }
  };

  if (detailsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!conversationDetails) {
    return (
      <Box>
        <Alert severity="error">Impossible de charger les détails de la conversation.</Alert>
        <Button onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Retour
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            variant="outlined"
          >
            Retour
          </Button>
          <Box>
            <Typography variant="h5">
              Conversation #{conversationDetails.id}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {conversationDetails.announcement.title}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => setInterventionDialogOpen(true)}
          color="warning"
        >
          Intervenir
        </Button>
      </Box>

      {/* Conversation Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <BusinessIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Annonce
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {conversationDetails.announcement.title}
                  </Typography>
                  <Chip
                    label={conversationDetails.announcement.type}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <MessageIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Participants
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {conversationDetails.user1.fullName || conversationDetails.user1.email} ↔ {conversationDetails.user2.fullName || conversationDetails.user2.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Messages */}
      <Paper sx={{ maxHeight: '600px', overflow: 'auto', p: 2 }}>
        {messagesLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {messagesData?.messages?.map((message: Message, index: number) => {
              const isModerator = message.sender.is_moderator;
              const isFromUser1 = message.sender.id === conversationDetails.user1.id;
              const isFromUser2 = message.sender.id === conversationDetails.user2.id;
              
              // Couleurs différentes pour chaque utilisateur
              let bubbleColor = 'primary.main';
              let textColor = 'white';
              let alignSelf = 'flex-start';
              
              if (isModerator) {
                bubbleColor = 'warning.main';
                textColor = 'white';
                alignSelf = 'center';
              } else if (isFromUser1) {
                bubbleColor = 'primary.main';
                textColor = 'white';
                alignSelf = 'flex-start';
              } else if (isFromUser2) {
                bubbleColor = 'secondary.main';
                textColor = 'white';
                alignSelf = 'flex-end';
              }

              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: alignSelf,
                    maxWidth: '70%',
                    mb: 1,
                  }}
                >
                  {/* En-tête du message */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.5,
                      alignSelf: alignSelf,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: bubbleColor,
                        fontSize: '0.75rem',
                      }}
                    >
                      {isModerator ? (
                        <AdminIcon sx={{ fontSize: '0.75rem' }} />
                      ) : (
                        <PersonIcon sx={{ fontSize: '0.75rem' }} />
                      )}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      {message.sender.fullName || message.sender.email}
                    </Typography>
                    {isModerator && (
                      <Chip
                        label="MODÉRATEUR"
                        size="small"
                        color="warning"
                        variant="filled"
                        sx={{ height: 16, fontSize: '0.6rem' }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {formatMessageTime(message.created_at)}
                    </Typography>
                  </Box>

                  {/* Bulle de message */}
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 1,
                    }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        bgcolor: bubbleColor,
                        color: textColor,
                        p: 1.5,
                        borderRadius: 2,
                        position: 'relative',
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          [alignSelf === 'flex-start' ? 'left' : 'right']: -8,
                          width: 0,
                          height: 0,
                          border: `8px solid transparent`,
                          borderBottomColor: bubbleColor,
                          borderTop: 'none',
                          [alignSelf === 'flex-start' ? 'borderLeft' : 'borderRight']: 'none',
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ color: textColor }}>
                        {message.content}
                      </Typography>
                    </Paper>

                    {/* Actions de modération */}
                    {!isModerator && (
                      <Tooltip title="Actions de modération">
                        <IconButton
                          size="small"
                          onClick={() => handleModerateMessage(message)}
                          sx={{
                            opacity: 0.7,
                            '&:hover': { opacity: 1 },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Statut de lecture */}
                  {message.is_read && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, alignSelf: alignSelf }}>
                      ✓ Lu
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Intervention Dialog */}
      <ResponsiveDialog
        open={interventionDialogOpen}
        onClose={() => setInterventionDialogOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>
          Intervenir dans la conversation
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Votre message sera visible par tous les participants de la conversation et sera marqué comme message de modération.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message de modération"
            value={interventionMessage}
            onChange={(e) => setInterventionMessage(e.target.value)}
            placeholder="Tapez votre message d'intervention..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterventionDialogOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleInterventionSubmit}
            variant="contained"
            disabled={!interventionMessage.trim() || interveneMutation.isPending}
            startIcon={interveneMutation.isPending ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Envoyer
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Moderation Dialog */}
      <ResponsiveDialog
        open={moderationDialogOpen}
        onClose={() => setModerationDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          Modérer le message
        </DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Message de {selectedMessage.sender.fullName || selectedMessage.sender.email}
              </Alert>
              
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2">
                  {selectedMessage.content}
                </Typography>
              </Paper>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Action de modération</InputLabel>
                <Select
                  value={moderationAction}
                  onChange={(e) => setModerationAction(e.target.value as ModerationAction['action'])}
                  label="Action de modération"
                >
                  <MenuItem value="delete">
                    <Box display="flex" alignItems="center" gap={1}>
                      <DeleteIcon color="error" />
                      Supprimer le message
                    </Box>
                  </MenuItem>
                  <MenuItem value="hide">
                    <Box display="flex" alignItems="center" gap={1}>
                      <HideIcon color="warning" />
                      Masquer le message
                    </Box>
                  </MenuItem>
                  <MenuItem value="warn">
                    <Box display="flex" alignItems="center" gap={1}>
                      <WarningIcon color="warning" />
                      Envoyer un avertissement
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Raison (optionnelle)"
                value={moderationReason}
                onChange={(e) => setModerationReason(e.target.value)}
                placeholder="Expliquez la raison de cette action de modération..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModerationDialogOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleModerationSubmit}
            variant="contained"
            color="error"
            disabled={moderateMessageMutation.isPending}
            startIcon={moderateMessageMutation.isPending ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {getModerationActionLabel(moderationAction)}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default ConversationViewer;
